import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RewardCard } from '../components/RewardCard';
import { X, MapPin, Globe, Mail, Phone, Leaf, Award, Filter, Search } from 'lucide-react';
import { rewardsData } from '../utils/rewardsData';
const ProfilePreview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [categoryFilter, setCategoryFilter] = useState('Tutti');
  const [costFilter, setCostFilter] = useState('Tutti');
  const [searchTerm, setSearchTerm] = useState('');
  // Get profile data from navigation state or use default
  const passedProfileData = location.state?.profileData;
  // Default profile data (fallback)
  const defaultProfileData = {
    logo: 'https://blog.lasaponaria.it/hubfs/LaSaponaria-5.jpg',
    name: 'La Saponaria',
    tagline: 'Cosmetici Naturali e Sostenibili dal 2007',
    description: 'Marchio italiano di cosmetici naturali, biologici e consapevoli. Società Benefit dal 2023, produciamo cosmetici artigianali con ingredienti biologici certificati, packaging ecologico e produzione 100% italiana con energia rinnovabile.',
    website: 'https://www.lasaponaria.it/',
    email: 'info@lasaponaria.it',
    phone: '+39 0721 1706607',
    certifications: ['Biologico Certificato', 'Società Benefit', 'Cruelty Free', 'Vegan OK'],
    sustainabilityItems: ['Produzione 100% artigianale e italiana con energia rinnovabile', 'Ingredienti biologici certificati e filiere etiche fair trade', 'Packaging plastic-free e opzioni zero-waste (cosmetici solidi, ricariche)', 'Prodotti biodegradabili che non inquinano fiumi, mari e laghi'],
    locations: [{
      id: 1,
      name: 'Milano Centro',
      address: 'Corso Garibaldi 64'
    }, {
      id: 2,
      name: 'Roma Cola di Rienzo',
      address: 'Via Cola di Rienzo 265/A'
    }, {
      id: 3,
      name: 'Bologna',
      address: 'Strada Maggiore 35/C'
    }, {
      id: 4,
      name: 'Firenze',
      address: 'Via Roma 15'
    }, {
      id: 5,
      name: 'Pesaro (Sede)',
      address: 'Via dei Trasporti 3-5'
    }, {
      id: 6,
      name: 'Milano Solari',
      address: 'Via Solari 15'
    }, {
      id: 7,
      name: 'Torino',
      address: 'Via Garibaldi 27'
    }]
  };
  // Merge passed data with defaults
  const profileData = passedProfileData ? {
    logo: passedProfileData.logo || defaultProfileData.logo,
    name: passedProfileData.name || defaultProfileData.name,
    tagline: defaultProfileData.tagline,
    description: passedProfileData.description || defaultProfileData.description,
    website: passedProfileData.website || defaultProfileData.website,
    email: defaultProfileData.email,
    phone: defaultProfileData.phone,
    certifications: passedProfileData.certifications || defaultProfileData.certifications,
    sustainabilityItems: passedProfileData.sustainabilityItems || defaultProfileData.sustainabilityItems,
    locations: passedProfileData.locations || defaultProfileData.locations
  } : defaultProfileData;
  // Get active rewards only
  const activeRewards = Object.values(rewardsData).filter(reward => reward.status === 'Attivo');
  // Filter rewards
  const filteredRewards = activeRewards.filter(reward => {
    const matchesCategory = categoryFilter === 'Tutti' || reward.type === categoryFilter;
    const matchesCost = costFilter === 'Tutti' || costFilter === '0-50' && reward.cost <= 50 || costFilter === '51-100' && reward.cost > 50 && reward.cost <= 100 || costFilter === '100+' && reward.cost > 100;
    const matchesSearch = searchTerm === '' || reward.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesCost && matchesSearch;
  });
  const categories = ['Tutti', ...Array.from(new Set(activeRewards.map(r => r.type)))];
  return <div className="min-h-screen bg-gray-50" data-id="element-1421">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm" data-id="element-1422">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center" data-id="element-1423">
          <div className="flex items-center gap-3" data-id="element-1424">
            <img src={profileData.logo} alt={profileData.name} className="h-12 w-12 rounded-lg object-cover" data-id="element-1425" />
            <div data-id="element-1426">
              <h1 className="text-xl font-bold text-gray-900" data-id="element-1427">
                {profileData.name}
              </h1>
              <p className="text-sm text-gray-500" data-id="element-1428">Profilo Partner</p>
            </div>
          </div>
          <button onClick={() => navigate('/profile')} className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors" title="Chiudi anteprima" data-id="element-1429">
            <X className="h-5 w-5" data-id="element-1430" />
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-greenback to-green-700 text-white py-16" data-id="element-1431">
        <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-1432">
          <img src={profileData.logo} alt={profileData.name} className="h-24 w-24 mx-auto mb-6 rounded-2xl shadow-xl object-cover bg-white p-2" data-id="element-1433" />
          <h1 className="text-4xl md:text-5xl font-semibold mb-3" data-id="element-1434">
            {profileData.name}
          </h1>
          <p className="text-xl md:text-2xl text-green-50" data-id="element-1435">
            {profileData.tagline}
          </p>

          {/* Certifications */}
          <div className="flex flex-wrap justify-center gap-2 mt-6" data-id="element-1436">
            {profileData.certifications.map((cert, index) => <span key={index} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium border border-white/30" data-id="element-1437">
                <Award className="h-3.5 w-3.5" data-id="element-1438" />
                {cert}
              </span>)}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="max-w-7xl mx-auto px-4 py-12" data-id="element-1439">
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm" data-id="element-1440">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1441">Chi Siamo</h2>
          <p className="text-gray-700 leading-relaxed mb-6" data-id="element-1442">
            {profileData.description}
          </p>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-gray-200" data-id="element-1443">
            <a href={profileData.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-600 hover:text-greenback transition-colors" data-id="element-1444">
              <Globe className="h-5 w-5 flex-shrink-0" data-id="element-1445" />
              <span className="text-sm truncate" data-id="element-1446">
                {profileData.website.replace('https://', '').replace('http://', '')}
              </span>
            </a>
            <a href={`mailto:${profileData.email}`} className="flex items-center gap-3 text-gray-600 hover:text-greenback transition-colors" data-id="element-1447">
              <Mail className="h-5 w-5 flex-shrink-0" data-id="element-1448" />
              <span className="text-sm" data-id="element-1449">{profileData.email}</span>
            </a>
            <a href={`tel:${profileData.phone}`} className="flex items-center gap-3 text-gray-600 hover:text-greenback transition-colors" data-id="element-1450">
              <Phone className="h-5 w-5 flex-shrink-0" data-id="element-1451" />
              <span className="text-sm" data-id="element-1452">{profileData.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Sustainability Section */}
      <section className="max-w-7xl mx-auto px-4 pb-12" data-id="element-1453">
        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl border border-green-200 p-8" data-id="element-1454">
          <div className="flex items-start gap-4" data-id="element-1455">
            <Leaf className="h-8 w-8 text-green-600 flex-shrink-0 mt-1" data-id="element-1456" />
            <div data-id="element-1457">
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1458">
                Impegno per la Sostenibilità
              </h3>
              <ul className="space-y-3 text-gray-700" data-id="element-1459">
                {profileData.sustainabilityItems.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-1460">
                    <span className="text-green-600 mt-0.5 text-lg" data-id="element-1461">✓</span>
                    <span data-id="element-1462">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="max-w-7xl mx-auto px-4 pb-12" data-id="element-1463">
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm" data-id="element-1464">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1465">
            Dove Trovarci
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-1466">
            {profileData.locations.map((location, index) => <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1467">
                <MapPin className="h-4 w-4 text-greenback flex-shrink-0 mt-0.5" data-id="element-1468" />
                <div className="flex-1 min-w-0" data-id="element-1469">
                  <p className="text-sm font-medium text-gray-900" data-id="element-1470">
                    {typeof location === 'string' ? location : location.name}
                  </p>
                  {typeof location === 'object' && location.address && <p className="text-xs text-gray-500 mt-0.5" data-id="element-1471">
                      {location.address}
                    </p>}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Rewards Section */}
      <section className="max-w-7xl mx-auto px-4 pb-16" data-id="element-1472">
        <div className="mb-8" data-id="element-1473">
          <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1474">
            Premi Disponibili
          </h2>
          <p className="text-gray-600" data-id="element-1475">
            {filteredRewards.length} premi attivi che puoi riscattare con i tuoi
            punti
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm" data-id="element-1476">
          <div className="flex flex-wrap gap-3" data-id="element-1477">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]" data-id="element-1478">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-1479" />
              <input type="text" placeholder="Cerca premio..." className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-greenback" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1480" />
            </div>

            {/* Category Filter */}
            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-1481">
              <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-1482" />
              <select className="bg-transparent text-sm focus:outline-none" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} data-id="element-1483">
                {categories.map(cat => <option key={cat} value={cat} data-id="element-1484">
                    {cat}
                  </option>)}
              </select>
            </div>

            {/* Cost Filter */}
            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-1485">
              <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-1486" />
              <select className="bg-transparent text-sm focus:outline-none" value={costFilter} onChange={e => setCostFilter(e.target.value)} data-id="element-1487">
                <option value="Tutti" data-id="element-1488">Tutti i costi</option>
                <option value="0-50" data-id="element-1489">0-50 punti</option>
                <option value="51-100" data-id="element-1490">51-100 punti</option>
                <option value="100+" data-id="element-1491">100+ punti</option>
              </select>
            </div>
          </div>
        </div>

        {/* Rewards Grid */}
        {filteredRewards.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1492">
            {filteredRewards.map(reward => <RewardCard key={reward.id} reward={reward} isPreview={true} data-id="element-1493" />)}
          </div> : <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-1494">
            <p className="text-gray-500" data-id="element-1495">
              Nessun premio trovato con i filtri selezionati.
            </p>
          </div>}
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8" data-id="element-1496">
        <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-1497">
          <p className="text-gray-400 text-sm" data-id="element-1498">
            © 2026 {profileData.name}. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>;
};
export default ProfilePreview;